import { Brain, Terminal } from 'lucide-react'
import { SimpleMarkdown } from '@/frontend/components/SimpleMarkdown'
import TokensWithBreakdown from '@/frontend/pages/CommandCenter/Telemetry/components/TokensWithBreakdown'
import { extractContent, formatTime, ToolResultBody } from './ConversationView'
import type { FleetSessionEvent, ToolCallBlock } from './ConversationView'

export { type FleetSessionEvent }

function eventTypeBadge(eventType: string) {
  switch (eventType) {
    case 'message':
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400">
          message
        </span>
      )
    case 'session':
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-400">
          session
        </span>
      )
    case 'model_change':
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-500/15 dark:text-purple-400">
          model_change
        </span>
      )
    default:
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 dark:bg-muted dark:text-foreground">
          {eventType}
        </span>
      )
  }
}

function roleBadge(role: string) {
  switch (role) {
    case 'user':
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 dark:bg-muted dark:text-foreground">
          user
        </span>
      )
    case 'assistant':
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-400">
          assistant
        </span>
      )
    case 'toolResult':
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700 dark:bg-yellow-500/15 dark:text-yellow-400">
          toolResult
        </span>
      )
    default:
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 dark:bg-muted dark:text-foreground">
          {role}
        </span>
      )
  }
}

function EventCard({ event, spansMultipleDays, showThinking, showToolCalls }: { event: FleetSessionEvent; spansMultipleDays: boolean; showThinking: boolean; showToolCalls: boolean }) {
  const isMessage = event.eventType === 'message'
  const isAssistant = event.role === 'assistant'
  const isUser = event.role === 'user'
  const isToolResult = event.role === 'toolResult'
  const isSystemEvent = !isMessage

  let cardClass = 'border border-gray-200 dark:border-border rounded-lg p-4 overflow-hidden'
  if (isMessage && isAssistant) {
    cardClass = 'bg-violet-50 dark:bg-violet-500/10 border-l-4 border-violet-300 dark:border-violet-500/30 rounded-lg p-4 overflow-hidden'
  } else if (isMessage && isUser) {
    cardClass = 'bg-gray-50 dark:bg-muted border border-gray-200 dark:border-border rounded-lg p-4 overflow-hidden'
  } else if (isMessage && isToolResult) {
    cardClass = 'border border-amber-200 dark:border-amber-500/30 rounded-lg p-4 overflow-hidden'
  } else if (isSystemEvent) {
    cardClass = 'border border-gray-100 dark:border-border rounded-lg p-3 bg-gray-50 dark:bg-muted overflow-hidden'
  }

  const { text, thinking, toolCalls, dataTruncated } = isMessage ? extractContent(event.data) : { text: '', thinking: null, toolCalls: [] as ToolCallBlock[], dataTruncated: false }
  const toolName = event.data?.message?.content?.[0]?.toolName || event.data?.message?.content?.[0]?.name

  return (
    <div className={cardClass}>
      <div className="flex items-center gap-2 mb-2">
        <span className="font-mono text-xs text-gray-400">
          {formatTime(event.timestamp, spansMultipleDays)}
        </span>
        {eventTypeBadge(event.eventType)}
        {event.role && roleBadge(event.role)}
        {event.model && (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 dark:bg-muted dark:text-foreground">
            {event.model}
          </span>
        )}
        {event.tokenCount != null && event.tokenCount > 0 && (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-50 text-indigo-600 dark:bg-indigo-500/15 dark:text-indigo-400">
            <TokensWithBreakdown total={event.tokenCount} breakdown={event.data?.message?.usage} /> tokens
          </span>
        )}
      </div>

      {isMessage && dataTruncated && (
        <div className="text-xs text-gray-400 italic">
          Conteudo truncado (evento excedeu limite de tamanho)
        </div>
      )}

      {isMessage && (isUser || isAssistant) && showToolCalls && toolCalls.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {toolCalls.map((tc) => (
            <details key={tc.id} className="inline open:block open:w-full">
              <summary className="cursor-pointer inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-cyan-100 text-cyan-700 dark:bg-cyan-500/15 dark:text-cyan-400">
                <Terminal className="h-3 w-3 mr-1" /> {tc.name}
              </summary>
              <pre className="text-xs font-mono overflow-auto mt-2 bg-gray-900 text-gray-100 p-3 rounded-lg max-h-64">
                {JSON.stringify(tc.arguments, null, 2)}
              </pre>
            </details>
          ))}
        </div>
      )}

      {isMessage && (isUser || isAssistant) && showThinking && thinking && (
        <details className="mb-2">
          <summary className="cursor-pointer text-xs text-gray-400 font-medium inline-flex items-center gap-1">
            <Brain className="h-3 w-3" /> Pensamento
          </summary>
          <div className="text-xs text-gray-500 dark:text-muted-foreground bg-gray-50 dark:bg-muted rounded p-3 mt-1 whitespace-pre-wrap max-h-64 overflow-auto">
            {thinking}
          </div>
        </details>
      )}

      {isMessage && isUser && text && (
        <div className="text-sm text-gray-900 dark:text-foreground whitespace-pre-wrap">{text}</div>
      )}

      {isMessage && isAssistant && text && (
        <div className="text-sm">
          <SimpleMarkdown content={text} />
        </div>
      )}

      {isMessage && isToolResult && (
        <details className="text-sm">
          <summary className="cursor-pointer text-amber-700 dark:text-amber-400 font-medium">
            {toolName ? `Resultado: ${toolName}` : 'Resultado de ferramenta'}
          </summary>
          <ToolResultBody content={event.data?.message?.content} />
        </details>
      )}

      {isSystemEvent && (
        <div className="text-xs text-gray-500 dark:text-muted-foreground">
          {event.eventType === 'session' && (
            <span>Evento de sessao</span>
          )}
          {event.eventType === 'model_change' && (
            <span>
              Modelo alterado{event.data?.modelId ? `: ${event.data.modelId}` : ''}
            </span>
          )}
          {event.eventType !== 'session' && event.eventType !== 'model_change' && (
            <span>{event.eventType}: {JSON.stringify(event.data).slice(0, 200)}</span>
          )}
        </div>
      )}
    </div>
  )
}

export function AllEventsView({ events, spansMultipleDays, showThinking, showToolCalls }: {
  events: FleetSessionEvent[]
  spansMultipleDays: boolean
  showThinking: boolean
  showToolCalls: boolean
}) {
  return (
    <div className="space-y-3">
      {events.map((event) => (
        <EventCard key={event.id} event={event} spansMultipleDays={spansMultipleDays} showThinking={showThinking} showToolCalls={showToolCalls} />
      ))}
    </div>
  )
}
